// NATS connect-config builder · spec 05 §1229-1254 + T304.
//
// Four @nats-io/nats-core defaults compile fine, ship green tests, and
// produce silent staleness in production. They MUST be overridden here.
// Verified against @nats-io/nats-core@3.4.0 source.
//
// Override 1 · pingInterval        — default 120_000 ms (2 min) → 5_000 ms (5 s)
// Override 2 · maxReconnectAttempts — default 10               → -1 (infinite)
// Override 3 · maxPingOut          — default 2                  → 2 (pinned)
// Override 4 · noEcho               — default false             → true
//
// ── RN platform switch — drain + reconnect, NEVER `nc.reset()` ─────────
// When the React Native adapter changes pingInterval (cellular → 30 s, see
// T6011) we MUST drain the existing connection and call `wsconnect(...)`
// again with the new config. `@nats-io/nats-core` does NOT expose a
// `nc.reset()` API — that was a Stage-0 spec hallucination; the canonical
// pattern is:
//     await nc.drain();          // graceful flush of pending msgs
//     nc = await wsconnect(buildConnectConfig({ ...env, pingIntervalMs: 30_000 }));
// The connectivity FSM (T8 connectivity state machine) owns the switch.

import type { Authenticator, ConnectionOptions } from '@nats-io/nats-core';

/**
 * Default ping cadence in milliseconds.
 *
 * `@nats-io/nats-core` defaults to 120_000 ms (2 min). At 2-min pings,
 * the DEGRADED pip flips ~6 min after the socket dies — a user watching
 * a hot market sees frozen prices for the whole window with no in-app
 * feedback. 5 s gives ~15 s p99 disconnect detection.
 *
 * RN cellular branches to 30 s via netInfo (T6011) — battery & app-store
 * review will reject 5 s as background-budget abuse.
 */
export const DEFAULT_PING_INTERVAL_MS = 5_000;

/**
 * Sentinel for unlimited reconnect attempts (`@nats-io/nats-core` convention).
 *
 * Upstream default is 10. After 10 failures (~30 s of outage), the client
 * silently disables realtime for the rest of the tab's lifetime. -1 lets
 * the connectivity machine's hysteresis own backoff (T8).
 */
export const INFINITE_RECONNECT = -1;

/**
 * Maximum pings outstanding before nats-core declares "stale connection".
 *
 * `@nats-io/nats-core` defaults to 2 — pinned here for clarity. With
 * `pingInterval=5000` and `maxPingOut=2`, a TCP-level dead socket is
 * surfaced as a stale-connection event in ~10-15 s (2 missed pings + the
 * next interval), feeding the connectivity FSM hysteresis budget.
 */
export const DEFAULT_MAX_PING_OUT = 2;

/**
 * Backoff fits inside the connectivity machine's hysteresis budget.
 * Pinned values; do not edit without coordinating with T17140.
 */
export const RECONNECT_TIME_WAIT_MS = 250;
export const RECONNECT_JITTER_MS = 500;
export const RECONNECT_JITTER_TLS_MS = 1_000;

/**
 * OVERRIDE 5 · connect `timeout` — explicit bound on a SINGLE connect attempt
 * (nats-core's own `timeout`, pinned rather than left implicit, in the same
 * spirit as the other overrides in this file). 10 s is ~5× a healthy devnet
 * handshake (~1-2 s); a TCP/WS open that stalls past it rejects, which the
 * cold-connect retry seam recovers.
 *
 * KNOWN LIMITATION — this does NOT cover a handshake that OPENS the socket but
 * then stalls waiting for the NATS `INFO` line (a degraded WSS gateway): a
 * Playwright `routeWebSocket` mock reproducing that state did not reject within
 * 30 s even with this timeout set, i.e. nats-core's `timeout` does not bound the
 * post-open INFO wait. Closing that hang path needs an explicit handshake
 * watchdog (a `Promise.race` in `connectSingleton` that rejects AND drains the
 * orphaned socket) — tracked as follow-up, not done here.
 */
export const CONNECT_TIMEOUT_MS = 10_000;

/**
 * Read the `NATS_PING_INTERVAL_MS` env override.
 *
 * Resolution order:
 *   1. explicit `pingIntervalMs` arg
 *   2. `process.env.NATS_PING_INTERVAL_MS` (operator tuning)
 *   3. `DEFAULT_PING_INTERVAL_MS` (5 s)
 *
 * Returns the default on any non-integer or non-positive value.
 */
export function resolvePingInterval(explicitMs: number | undefined): number {
  if (typeof explicitMs === 'number' && Number.isFinite(explicitMs) && explicitMs > 0) {
    return Math.trunc(explicitMs);
  }
  const env =
    typeof process !== 'undefined' && process.env ? process.env.NATS_PING_INTERVAL_MS : undefined;
  if (typeof env === 'string' && env.length > 0) {
    const parsed = Number.parseInt(env, 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return DEFAULT_PING_INTERVAL_MS;
}

export interface BuildConnectConfigOptions {
  readonly servers: readonly string[];
  /**
   * JWT closure (T303). Called by the nats client on each (re)connect to
   * obtain a fresh, short-lived JWT. The closure MUST be side-effect-free.
   */
  readonly authenticator: () => string;
  /** Optional override; env `NATS_PING_INTERVAL_MS` also honoured. */
  readonly pingIntervalMs?: number;
}

/**
 * Build a `ConnectionOptions` object for `@nats-io/nats-core`'s `wsconnect`.
 *
 * Three explicit overrides; everything else accepts upstream defaults.
 * Pass platform-specific tweaks (e.g. RN cellular pingInterval) via the
 * `pingIntervalMs` arg.
 */
export function buildConnectConfig(opts: BuildConnectConfigOptions): ConnectionOptions {
  const jwtClosure = opts.authenticator;
  const authenticator: Authenticator = (_nonce?: string) => ({
    jwt: jwtClosure(),
  });

  return {
    servers: [...opts.servers],
    authenticator,

    // ─────────────────────────────────────────────────────────────────
    // OVERRIDE 1 · pingInterval — default 120_000 (2 min) gives ~6 min
    // p99 disconnect detection. 5 s gives ~15 s. RN cellular branches
    // to 30 s via netInfo (T6011); web honours NATS_PING_INTERVAL_MS env.
    // ─────────────────────────────────────────────────────────────────
    pingInterval: resolvePingInterval(opts.pingIntervalMs),

    // ─────────────────────────────────────────────────────────────────
    // OVERRIDE 2 · maxReconnectAttempts — default 10 silently disables
    // realtime after ~30 s outage. -1 = infinite; connectivity-machine
    // hysteresis owns backoff (T8 connectivity state machine).
    // ─────────────────────────────────────────────────────────────────
    maxReconnectAttempts: INFINITE_RECONNECT,

    // Backoff fits inside connectivity machine's hysteresis budget:
    reconnectTimeWait: RECONNECT_TIME_WAIT_MS, // base delay between attempts
    reconnectJitter: RECONNECT_JITTER_MS, // ±500 ms random
    // ─────────────────────────────────────────────────────────────────
    // T19903 · `reconnectJitterTLS: 1_000` is INTENTIONALLY REDUNDANT.
    // The value matches `@nats-io/nats-core`'s current upstream default
    // exactly, so this assignment is a structural no-op TODAY. It is
    // kept on purpose: the spec enumerates the value at file 05 §1234,
    // and a future upstream change to the default would silently shift
    // the TLS reconnect jitter without anyone noticing. Pinning the
    // value here means a code-review diff catches the upstream drift,
    // and the connectivity FSM hysteresis budget math (which folds in
    // the jitter window) stays anchored to a fixed number. Removing
    // this line because "it matches the default" is a regression.
    // ─────────────────────────────────────────────────────────────────
    reconnectJitterTLS: RECONNECT_JITTER_TLS_MS,

    // ─────────────────────────────────────────────────────────────────
    // OVERRIDE 3 · maxPingOut — pinned at 2 (nats-core default).
    // Surfaced explicitly so a future upstream bump (e.g. to 4) doesn't
    // silently double the "stale connection" detection window.
    // ─────────────────────────────────────────────────────────────────
    maxPingOut: DEFAULT_MAX_PING_OUT,

    // ─────────────────────────────────────────────────────────────────
    // OVERRIDE 5 · timeout — reject a hung handshake so the cold-connect
    // retry (which fires on REJECTION) can recover. See CONNECT_TIMEOUT_MS.
    // ─────────────────────────────────────────────────────────────────
    timeout: CONNECT_TIMEOUT_MS,

    // ─────────────────────────────────────────────────────────────────
    // OVERRIDE 4 · noEcho — don't round-trip our own publishes.
    // Mutations that publish to NATS (cross-tab cache-write broadcast)
    // MUST NOT be re-delivered to the publishing tab. Without noEcho,
    // setQueryData from a same-tab mutation re-triggers the routing
    // table's NATS dispatch → double-apply, racy rollback.
    // ─────────────────────────────────────────────────────────────────
    noEcho: true,

    // ─────────────────────────────────────────────────────────────────
    // OVERRIDE 6 · ignoreClusterUpdates — the broker is a cluster behind
    // one public WebSocket endpoint. Gossiped `connect_urls` are the
    // nodes' private task addresses, unreachable from a browser; trying
    // them first would delay every reconnect. Keep the configured server
    // list only.
    // ─────────────────────────────────────────────────────────────────
    ignoreClusterUpdates: true,
  };
}
